import React from 'react';
import { Badge } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { num } from '../../lib/aiPricingView';
import { Code } from './shared';

/** The wire id without its provider prefix — `google/gemini-3-flash-preview` → `gemini-3-flash-preview`. */
export const shortModelId = (modelId) => String(modelId || '').split('/').pop();

/**
 * Up to `max` chips of `model · share%`, then «+N».
 *
 * The chip is composed from NODES, not a template string, because the model id
 * must be an LTR island and the share must be a localised percentage — one
 * template cannot hold both. A chip goes danger-toned when the model is
 * unpriced: that chip is the reason the service's cost is NULL.
 */
export default function ModelShareChips({ models = [], max = 3 }) {
  const { t, formatPercent } = useTranslation();
  const shown = models.slice(0, max);
  const rest = models.slice(max);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((m) => {
        const share = num(m.shareOfCostPct);
        return (
          <Badge
            key={`${m.modelId}:${m.role}`}
            variant={m.unpriced ? 'destructive' : 'outline'}
            title={m.modelId}
            className="gap-1"
          >
            <Code>{shortModelId(m.modelId)}</Code>
            <span aria-hidden>·</span>
            <span className="tabular-nums">
              {m.unpriced ? t('aiPricing.models.status.unknown') : (share == null ? t('common.value.none') : formatPercent(share))}
            </span>
          </Badge>
        );
      })}
      {rest.length ? (
        <Badge variant="outline" title={rest.map((m) => m.modelId).join(', ')}>
          {t('aiPricing.services.moreModels', { count: rest.length })}
        </Badge>
      ) : null}
    </span>
  );
}
